import { Collapse, ListItemButton, ListItemText, styled } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { NavIcon } from "./Account";
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import SettingsIcon from '@mui/icons-material/Settings'
import type UserStoreStore from "../UserData/_stores/Store/UserStoreStore";
import EditNoteIcon from '@mui/icons-material/EditNote';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import { useAppSelector } from "../Common/_hooks/useAppSelector";
import { STORE_ICON_URLS } from './StoreIconUrls'

const StoreIcon = styled('img')({
  width: '24px',
  height: '24px',
  objectFit: 'contain',
})

interface Props {
  store: UserStoreStore
  opened: boolean
  onToggle: (storeSlug: string) => void
}

export default function StoreNav(props: Props) {
  const [t] = useTranslation('account-v2')
  const [tR] = useTranslation('routes')
  const storeSlug = props.store.get('slug')
  const storeName = props.store.get('store_name')?.trim() ?? ''
  const storeIconUrl = STORE_ICON_URLS[storeSlug]
  const storeIntegration = useAppSelector(state => state.get('appData').get('storeIntegrations').get(storeSlug))
  const hasSetupPage = !!storeIntegration && !storeIntegration.get('legacy')

  const onClickHeader = useCallback(() => props.onToggle(storeSlug), [props.onToggle, storeSlug])

  return <>
    <ListItemButton onClick={onClickHeader}>
      <NavIcon><StoreIcon src={storeIconUrl} /></NavIcon>
      <ListItemText
        sx={{ minWidth: 0, mr: 1, my: 0 }}
        secondary={storeName !== '' ? storeName : undefined}
        primaryTypographyProps={{
          sx: {
            fontWeight: props.opened ? 600 : 400
          }
        }}
        secondaryTypographyProps={{
          noWrap: true,
          sx: {
            color: 'text.secondary',
            display: 'block',
            fontSize: '0.75rem',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        }}
      >{props.store.get('name')}</ListItemText>
      <ExpandMoreRoundedIcon
        fontSize="large"
        sx={{
          transform: props.opened ? 'scaleY(-1)' : 'scaleY(1)',
          transition: 'transform 250ms'
        }}
      />
    </ListItemButton>
    <Collapse in={props.opened} timeout="auto" sx={{ml: 2}}>
      <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/overview', { store: storeSlug })}>
        <NavIcon><ShowChartIcon /></NavIcon>
        <ListItemText>{t('Overview')}</ListItemText>
      </ListItemButton>
      <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/products', { store: storeSlug })}>
        <NavIcon><ChecklistIcon /></NavIcon>
        <ListItemText>{t('Products')}</ListItemText>
      </ListItemButton>
      <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/orders', { store: storeSlug })}>
        <NavIcon><ShoppingBasketIcon /></NavIcon>
        <ListItemText>{t('Orders')}</ListItemText>
      </ListItemButton>
      <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/reports', { store: storeSlug })}>
        <NavIcon><AssessmentIcon /></NavIcon>
        <ListItemText>{t('Purchase Report')}</ListItemText>
      </ListItemButton>
      <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/presets', { store: storeSlug })}>
        <NavIcon><EditNoteIcon /></NavIcon>
        <ListItemText>{t('Presets')}</ListItemText>
      </ListItemButton>
      {hasSetupPage ? <ListItemButton component={NavLink} to={tR('/account/stores/connect/{{store}}', { store: storeSlug })}>
        <NavIcon><ConstructionOutlinedIcon /></NavIcon>
        <ListItemText>{t('Setup')}</ListItemText>
      </ListItemButton> : null}
      <ListItemButton component={NavLink} to={tR('/account/stores/{{store}}/settings', { store: storeSlug })}>
        <NavIcon><SettingsIcon /></NavIcon>
        <ListItemText>{t('Settings')}</ListItemText>
      </ListItemButton>
    </Collapse>
  </>
}
